import { styled, keyframes, alpha } from '@mui/material/styles';
import { 
  Box, 
  Paper, 
  Card, 
  Button, 
  TextField, 
  Typography,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Tooltip,
  Fade,
  Grow,
  Slide,
  Zoom,
} from '@mui/material';

// Animations
export const fadeInUp = keyframes`
  from {
    opacity: 0;
    transform: translateY(30px);
  }
  to {
    opacity: 1;
    transform: translateY(0);
  }
`;

export const slideInLeft = keyframes`
  from {
    opacity: 0;
    transform: translateX(-30px);
  }
  to {
    opacity: 1;
    transform: translateX(0);
  }
`;

export const pulseGlow = keyframes`
  0%, 100% { 
    box-shadow: 0 0 20px rgba(102, 126, 234, 0.3); 
  }
  50% { 
    box-shadow: 0 0 40px rgba(102, 126, 234, 0.6); 
  }
`;

export const floating = keyframes`
  0%, 100% { 
    transform: translateY(0px) rotate(0deg); 
  }
  50% { 
    transform: translateY(-20px) rotate(5deg); 
  }
`;

// Modern Glassmorphism Components
export const GlassCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-1px)',
    boxShadow: '0 6px 20px rgba(0, 0, 0, 0.12)',
  },
}));

export const GlassPaper = styled(Paper)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 8,
  boxShadow: '0 4px 12px rgba(0, 0, 0, 0.08)',
  overflow: 'hidden',
}));

export const ModernButton = styled(Button)(({ theme }) => ({
  borderRadius: 12,
  textTransform: 'none',
  fontWeight: 600,
  padding: '12px 24px',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'linear-gradient(135deg, #5a67d8 0%, #667eea 100%)',
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 25px rgba(102, 126, 234, 0.4)',
  },
  '&:active': {
    transform: 'translateY(0)',
  },
}));

export const ModernTextField = styled(TextField)(({ theme }) => ({
  '& .MuiOutlinedInput-root': {
    borderRadius: 12,
    background: 'rgba(255, 255, 255, 0.9)',
    backdropFilter: 'blur(10px)',
    border: '1px solid rgba(255, 255, 255, 0.2)',
    transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
    '&:hover': {
      transform: 'translateY(-2px)',
      boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
    },
    '&.Mui-focused': {
      transform: 'translateY(-2px)',
      boxShadow: '0 12px 24px rgba(102, 126, 234, 0.15)',
      '& fieldset': {
        borderColor: theme.palette.primary.main,
        borderWidth: '2px',
      },
    },
  },
  '& .MuiInputLabel-root': {
    fontWeight: 500,
    '&.Mui-focused': {
      color: theme.palette.primary.main,
    },
  },
}));

export const GradientTypography = styled(Typography)(({ theme }) => ({
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  backgroundClip: 'text',
  WebkitBackgroundClip: 'text',
  WebkitTextFillColor: 'transparent',
  fontWeight: 700,
}));

export const ModernChip = styled(Chip)(({ theme }) => ({
  borderRadius: 20,
  fontWeight: 500,
  background: 'rgba(255, 255, 255, 0.9)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 8px 16px rgba(0, 0, 0, 0.1)',
  },
}));

export const ModernTable = styled(Table)(({ theme }) => ({
  '& .MuiTableHead-root': {
    '& .MuiTableRow-root': {
      background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
      '& .MuiTableCell-head': {
        color: 'white !important',
        fontWeight: 600,
        borderBottom: 'none',
        textShadow: '0 1px 2px rgba(0, 0, 0, 0.3)',
        '& .MuiTypography-root': {
          color: 'white !important',
        },
        '& .MuiChip-root': {
          color: 'white !important',
        },
      },
    },
  },
  '& .MuiTableBody-root': {
    '& .MuiTableRow-root': {
      transition: 'all 0.2s ease-in-out',
      '&:hover': {
        background: 'rgba(102, 126, 234, 0.05)',
        transform: 'translateY(-1px)',
      },
    },
  },
}));

export const StatusChip = styled(Chip)(({ theme, color = 'default' }) => ({
  borderRadius: 20,
  fontWeight: 600,
  fontSize: '0.75rem',
  height: 24,
  '&.success': {
    background: 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)',
    color: 'white',
  },
  '&.warning': {
    background: 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)',
    color: 'white',
  },
  '&.error': {
    background: 'linear-gradient(135deg, #F44336 0%, #E57373 100%)',
    color: 'white',
  },
  '&.info': {
    background: 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)',
    color: 'white',
  },
}));

export const IconBox = styled(Box)(({ theme }) => ({
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  width: 40,
  height: 40,
  borderRadius: '50%',
  background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
  color: 'white',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'scale(1.1) rotate(5deg)',
    boxShadow: '0 8px 16px rgba(102, 126, 234, 0.3)',
  },
}));

export const AnimatedBox = styled(Box)(({ theme }) => ({
  animation: `${fadeInUp} 0.6s ease-out`,
}));

export const FloatingBox = styled(Box)(({ theme }) => ({
  animation: `${floating} 3s ease-in-out infinite`,
}));

export const PulseBox = styled(Box)(({ theme }) => ({
  animation: `${pulseGlow} 2s ease-in-out infinite`,
}));

// Layout Components
export const PageContainer = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  padding: theme.spacing(1),
  [theme.breakpoints.down('md')]: {
    padding: theme.spacing(0.5),
  },
}));

export const ContentContainer = styled(Box)(({ theme }) => ({
  maxWidth: 1400,
  margin: '0 auto',
  width: '100%',
}));

export const SectionHeader = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  marginBottom: theme.spacing(1),
  padding: theme.spacing(1.5),
  background: 'rgba(255, 255, 255, 0.9)',
  borderRadius: 12,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 4px 16px rgba(0, 0, 0, 0.1)',
}));

// Data Display Components
export const MetricCard = styled(Card)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 16,
  padding: theme.spacing(3),
  textAlign: 'center',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-4px)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
  },
}));

interface AlertCardProps {
  severity?: 'error' | 'warning' | 'success' | 'info';
}

export const AlertCard = styled(Card, {
  shouldForwardProp: (prop) => prop !== 'severity',
})<AlertCardProps>(({ theme, severity = 'info' }) => ({
  background: severity === 'error' 
    ? 'linear-gradient(135deg, #ff6b6b 0%, #ee5a52 100%)'
    : severity === 'warning'
    ? 'linear-gradient(135deg, #FF9800 0%, #FFB74D 100%)'
    : severity === 'success'
    ? 'linear-gradient(135deg, #4CAF50 0%, #81C784 100%)'
    : 'linear-gradient(135deg, #2196F3 0%, #64B5F6 100%)',
  color: 'white',
  borderRadius: 16,
  backdropFilter: 'blur(10px)',
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    transform: 'translateY(-2px)',
    boxShadow: '0 25px 50px rgba(0, 0, 0, 0.15)',
  },
}));

// Navigation Components
export const ModernListItem = styled(ListItem)(({ theme }) => ({
  borderRadius: 12,
  margin: theme.spacing(0.5, 0),
  transition: 'all 0.3s cubic-bezier(0.4, 0, 0.2, 1)',
  '&:hover': {
    background: 'rgba(102, 126, 234, 0.1)',
    transform: 'translateX(8px)',
  },
  '&.Mui-selected': {
    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
    color: 'white',
    '&:hover': {
      background: 'linear-gradient(135deg, #5a67d8 0%, #667eea 100%)',
    },
  },
}));

// Form Components
export const FormContainer = styled(Box)(({ theme }) => ({
  background: 'rgba(255, 255, 255, 0.95)',
  backdropFilter: 'blur(10px)',
  border: '1px solid rgba(255, 255, 255, 0.2)',
  borderRadius: 16,
  padding: theme.spacing(4),
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.1)',
  maxWidth: 600,
  margin: '0 auto',
}));

// Utility Components
export const LoadingSpinner = styled(Box)(({ theme }) => ({
  display: 'flex',
  justifyContent: 'center',
  alignItems: 'center',
  padding: theme.spacing(4),
  '& .MuiCircularProgress-root': {
    color: theme.palette.primary.main,
  },
}));

export const EmptyState = styled(Box)(({ theme }) => ({
  textAlign: 'center',
  padding: theme.spacing(6),
  color: theme.palette.text.secondary,
  '& .MuiSvgIcon-root': {
    fontSize: 64,
    marginBottom: theme.spacing(2),
    opacity: 0.5,
  },
}));

// Export all components
export {
  Box,
  Paper,
  Card,
  Button,
  TextField,
  Typography,
  Chip,
  TableContainer,
  Table,
  TableHead,
  TableBody,
  TableRow,
  TableCell,
  IconButton,
  Avatar,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemIcon,
  Badge,
  Tooltip,
  Fade,
  Grow,
  Slide,
  Zoom,
};
